import styled from "@emotion/styled";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import SlickSlider from "@components/slick-slider";
import { Skeleton, useMediaQuery } from "@mui/material";
import { LazyLoadImage } from "react-lazy-load-image-component";
import { PhotoSlider } from "react-photo-view";
import { IPictures } from "shared/models/interfaces/response/realestate.interface";
import React from "react";

interface AlbumPicturesProps {
  pictures: IPictures[];
  mainImage: IPictures;
  onMainImageChange: (picture: IPictures) => void;
  data: any;
}

function CustomNextArrow(props: any) {
  return <NextArrow {...props} />;
}

function CustomPrevArrow(props: any) {
  return <PrevArrow {...props} />;
}

const AlbumPictures = ({
  pictures,
  mainImage,
  onMainImageChange,
  data,
}: AlbumPicturesProps) => {
  const isMobile = useMediaQuery("(max-width: 991px)");
  const [isVisible, setIsVisible] = React.useState(false);
  const [index, setIndex] = React.useState(0);
  const uniquePictures = React.useMemo(() => {
    return pictures;
  }, [pictures]);

  // Preparar las imágenes para el PhotoSlider
  const photoImages = React.useMemo(() => {
    return uniquePictures.map((picture) => ({
      src: picture.pictures,
      key: picture.id,
    }));
  }, [uniquePictures]);

  const verticalSliderSettings = {
    slidesToShow: 4,
    slidesToScroll: 3,
    vertical: true,
    verticalSwiping: true,
    swipeToSlide: true,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
  };

  const sliderSettings = {
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    swipeToSlide: true,
    nextArrow: <CustomNextArrow />,
    prevArrow: <CustomPrevArrow />,
  };

  return (
    <AlbumBody>
      <div hidden>
        <PhotoSlider
          speed={() => 400}
          easing={(type) => "cubic-bezier(0.25, 0.8, 0.25, 1)"}
          maskOpacity={0.8}
          loop={true}
          images={uniquePictures.map((item) => ({
            src: item.pictures,
            key: item.id,
          }))}
          visible={isVisible}
          onClose={() => setIsVisible(false)}
          onIndexChange={(index) => setIndex(index)}
          index={index}
        />
      </div>

      <HorizontalSliderMobile {...sliderSettings}>
        {uniquePictures.map((picture: IPictures) => (
          <AlbumImage
            src={picture.pictures}
            alt={`${data.realstate_type} ${data.sector}`}
            key={picture.id}
            delayTime={5000}
            onClick={() => {
              setIsVisible(true);
              setIndex(
                uniquePictures.findIndex(
                  (item) => item.pictures === picture.pictures
                )
              );
            }}
            placeholder={
              <Skeleton
                variant="rectangular"
                animation="wave"
                style={{
                  display: "block",
                  width: "100%",
                  height: "100%",
                }}
              />
            }
          />
        ))}
      </HorizontalSliderMobile>

      <MainImageContainer>
        {uniquePictures.map((picture: IPictures) => (
          <AlbumContentActive
            key={picture.id}
            style={{
              display:
                mainImage?.pictures === picture?.pictures ? "block" : "none",
            }}
            onClick={() => {
              setIsVisible(true);
              setIndex(
                uniquePictures.findIndex(
                  (item) => item.pictures === picture.pictures
                )
              );
            }}
          >
            <AlbumActive
              delayTime={5000}
              src={mainImage?.pictures}
              alt={`${data.realstate_type} ${data.sector}`}
              placeholder={
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  style={{
                    display: "block",
                    width: "100%",
                    height: "100%",
                  }}
                />
              }
            />
          </AlbumContentActive>
        ))}
      </MainImageContainer>

      <AlbumPreview>
        <VerticalSlider
          {...verticalSliderSettings}
          infinite={uniquePictures.length < 4 ? false : true}
        >
          {uniquePictures.map((picture: IPictures, index: number) => (
            <div
              key={picture.id}
              onClick={() => {
                onMainImageChange(picture);
                // setIsVisible(true);
                // setIndex(
                //   uniquePictures.findIndex(
                //     (item) => item.pictures === picture.pictures
                //   )
                // );
              }}
            >
              <AlbumImage
                src={picture.pictures}
                alt={`${data.realstate_type} ${data.sector}`}
                placeholder={
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    style={{
                      display: "block",
                      width: "100%",
                      height: "100%",
                    }}
                  />
                }
              />
            </div>
          ))}
        </VerticalSlider>
      </AlbumPreview>
    </AlbumBody>
  );
};

const MainImageContainer = styled.div`
  width: 80%;
  height: 100%;
  overflow: hidden;
  border-radius: 0 0 0px 20px;
`;

const AlbumBody = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  height: 50vw;
  max-height: 600px;
  justify-content: space-between;
  align-items: stretch;
  background-color: var(--color-brand-bg-secondary);
  border-radius: 0 0 20px 20px;
  position: relative;
  @media screen and (max-width: 791px) {
    border-radius: 0 0 20px 0px;
  }
  .slick-list {
    border-radius: 0 0 20px 0px;
    overflow: hidden;
  }
`;

const HorizontalSliderMobile = styled(SlickSlider)`
  display: none;
  height: 100%;
  width: 100%;

  & .slick-list,
  .slick-track,
  .slick-slide > div {
    height: inherit;
  }

  & .slick-list {
    width: inherit;
  }

  @media screen and (max-width: 991px) {
    display: block;
  }
`;

const VerticalSlider = styled(SlickSlider)`
  height: 100%;

  & .slick-list {
    height: 100% !important;
  }

  & .slick-slide {
    border: none;

    & > div {
      line-height: 0;
    }
  }

  & .slick-prev {
    display: none !important;
  }
`;

const NextArrow = styled(ArrowForwardIosIcon)`
  color: var(--color-brand-blue-main);
  top: 100%;
  left: 50%;
  transform: translateX(-50%) rotate(90deg);
  height: 26px;
  width: 26px;

  &:hover {
    color: var(--color-brand-blue-main);
  }

  @media screen and (max-width: 991px) {
    top: 50%;
    transform: rotate(0) translateY(-50%);
    left: calc(100% - 50px);
    color: var(--color-brand-bg-secondary);
    height: 50px;
    width: 50px;

    &:hover {
      color: var(--color-brand-bg-secondary);
    }
  }
`;

const PrevArrow = styled(ArrowBackIosNewIcon)`
  top: 50%;
  transform: translateY(-50%);
  left: 0;
  color: var(--color-brand-bg-secondary);
  height: 50px;
  width: 50px;
  z-index: 1;

  &:hover,
  &:focus {
    color: var(--color-brand-bg-secondary);
  }
`;

const AlbumContentActive = styled.div`
  width: 100%;
  height: 50vw;
  max-height: 600px;
  cursor: pointer;
  @media screen and (max-width: 1180px) {
    // max-height: 600px;
  }
  @media screen and (max-width: 1054px) {
    // min-height: 70vh;
    // height: 70vh;
  }
  @media screen and (max-width: 991px) {
    display: none;
  }
`;

const AlbumActive = styled(LazyLoadImage)`
  object-fit: cover;
  position: relative;
  width: 100%;
  height: 100%;
  background: white;
  @media screen and (max-width: 991px) {
    display: none;
  }
`;

const AlbumPreview = styled.div`
  width: calc(20% - 10px);
  height: 100%;

  @media screen and (max-width: 991px) {
    display: none;
  }
`;

const AlbumImage = styled(LazyLoadImage as any)`
  object-fit: cover;
  width: 100%;
  height: 145px;
  margin-bottom: 5px;
  cursor: pointer;
  @media screen and (max-width: 991px) {
    height: inherit;
    margin: 0;
    cursor: auto;
  }
`;

export default AlbumPictures;
